/**
 * Visual themes for Faanuu pieces (coquillages / cailloux polynésiens).
 * Each theme defines styles for player A and player B.
 */

/** @typedef {'classic' | 'lagoon' | 'volcan' | 'reef'} PieceThemeId */

/** @type {Record<PieceThemeId, { id: PieceThemeId, label: string, description: string, A: object, B: object }>} */
export const PIECE_THEMES = {
  classic: {
    id: "classic",
    label: "Classique",
    description: "Coquillages nacrés et galets de plage",
    A: {
      kind: "shell",
      label: "Coquillages blancs",
      shell: "#f7f2e8",
      shellEdge: "#c9b896",
      ridge: "rgba(180,160,130,0.35)",
      border: "1px solid #b9a98a",
      shadow: "0 4px 10px rgba(0,0,0,0.35)",
    },
    B: {
      kind: "pebble",
      label: "Galets noirs",
      pebble: "#2a2a2a",
      pebbleLight: "#5a5a5a",
      pebbleDark: "#0a0a0a",
      speck: "rgba(255,255,255,0.08)",
      border: "1px solid #1a1a1a",
      shadow: "0 4px 12px rgba(0,0,0,0.55)",
    },
  },
  lagoon: {
    id: "lagoon",
    label: "Lagoon",
    description: "Coquillages turquoise et cailloux de rivière",
    A: {
      kind: "shell",
      label: "Coquillages lagoon",
      shell: "#e8f6f3",
      shellEdge: "#7ec4b8",
      ridge: "rgba(90,170,155,0.4)",
      border: "1px solid #6ab5a8",
      shadow: "0 4px 10px rgba(40,120,100,0.35)",
    },
    B: {
      kind: "pebble",
      label: "Cailloux polis",
      pebble: "#4a5568",
      pebbleLight: "#718096",
      pebbleDark: "#2d3748",
      speck: "rgba(200,220,255,0.12)",
      border: "1px solid #2d3748",
      shadow: "0 4px 12px rgba(0,0,0,0.45)",
    },
  },
  volcan: {
    id: "volcan",
    label: "Volcan",
    description: "Coquillages roses et pierres de lave",
    A: {
      kind: "shell",
      label: "Coquillages corail",
      shell: "#fde8e4",
      shellEdge: "#e8a598",
      ridge: "rgba(200,100,80,0.35)",
      border: "1px solid #d4897a",
      shadow: "0 4px 10px rgba(120,40,30,0.3)",
    },
    B: {
      kind: "pebble",
      label: "Pierres de lave",
      pebble: "#1a1210",
      pebbleLight: "#4a3028",
      pebbleDark: "#050403",
      speck: "rgba(245,120,60,0.15)",
      border: "1px solid #2a1810",
      shadow: "0 4px 14px rgba(0,0,0,0.65)",
    },
  },
  reef: {
    id: "reef",
    label: "Récif",
    description: "Coquillages spiralés et galets mouchetés",
    A: {
      kind: "shell",
      label: "Coquillages spiralés",
      shell: "#fff9ef",
      shellEdge: "#ddb88a",
      ridge: "rgba(210,150,90,0.45)",
      border: "1px solid #c9a06a",
      shadow: "0 4px 10px rgba(80,50,20,0.3)",
    },
    B: {
      kind: "pebble",
      label: "Galets mouchetés",
      pebble: "#3d3832",
      pebbleLight: "#6b6358",
      pebbleDark: "#1e1b18",
      speck: "rgba(255,230,180,0.14)",
      border: "1px solid #252220",
      shadow: "0 4px 12px rgba(0,0,0,0.5)",
    },
  },
};

export const PIECE_THEME_ORDER = ["classic", "lagoon", "volcan", "reef"];
export const DEFAULT_PIECE_THEME = "classic";
export const PIECE_THEME_STORAGE_KEY = "faanuu.pieceTheme";

export function isValidPieceTheme(id) {
  return Boolean(id && PIECE_THEMES[id]);
}

export function getPieceTheme(id) {
  return PIECE_THEMES[isValidPieceTheme(id) ? id : DEFAULT_PIECE_THEME];
}

export function loadLocalPieceTheme() {
  try {
    const raw = typeof window !== "undefined" && window.localStorage.getItem(PIECE_THEME_STORAGE_KEY);
    if (isValidPieceTheme(raw)) return raw;
  } catch (_) {
    /* ignore */
  }
  return DEFAULT_PIECE_THEME;
}

export function saveLocalPieceTheme(id) {
  try {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(PIECE_THEME_STORAGE_KEY, id);
    }
  } catch (_) {
    /* ignore */
  }
}

export function getOwnerLabels(themeId) {
  const theme = getPieceTheme(themeId);
  return { A: theme.A.label, B: theme.B.label };
}
